import LOCAL_STORAGE from "@utils/localStorage";

// Platform detection
export const PLATFORMS = {
  ANDROID: "android",
  WEBOS: "Webos",
  TIZEN: "Tizen",
  WEB: "web",
};

export const getPlatform = () => {
  const deviceOS = LOCAL_STORAGE.DEVICE_OS.GET();

  if (deviceOS === PLATFORMS.ANDROID) return PLATFORMS.ANDROID;
  if (deviceOS === PLATFORMS.WEBOS) return PLATFORMS.WEBOS;
  if (deviceOS === PLATFORMS.TIZEN) return PLATFORMS.TIZEN;
  return PLATFORMS.WEB;
};

// Get current platform
export const currentPlatform = getPlatform();

// Platform specific behaviors and features
export const platformConfig = {
  [PLATFORMS.ANDROID]: {
    isLauncher: window.Android?.isLauncher || false,
    isStandalone: !window.Android?.isLauncher,
    supportsAppLaunching: true,
    supportsBackButton: true,
    exitApp: () => window.Android?.exit(),
    goToHome: () => window.Android?.goToHome(),
    getMacAddress: () => window.Android?.getMacAddress(),
    getAppVersion: () => window.Android?.getAppVersion(),
    getDeviceId: () => LOCAL_STORAGE.DEVICE_ID.GET(),
    getDeviceModel: () => LOCAL_STORAGE.DEVICE_MODEL.GET(),
    getDeviceName: () => LOCAL_STORAGE.DEVICE_NAME.GET(),
    getDeviceIp: () => LOCAL_STORAGE.DEVICE_IP.GET(),
  },
  [PLATFORMS.WEBOS]: {
    isLauncher: false, // WebOS apps are never launchers
    isStandalone: true,
    supportsAppLaunching: true,
    supportsBackButton: true,
    exitApp: () => window.webOS?.platformBack(),
    goToHome: () => window.webOS?.platformBack(),
    getMacAddress: () => LOCAL_STORAGE.DEVICE_ID.GET(), // Using device ID as MAC for WebOS
    getAppVersion: () => window.webOS?.deviceInfo?.version,
    getDeviceId: () => LOCAL_STORAGE.DEVICE_ID.GET(),
    getDeviceModel: () => LOCAL_STORAGE.DEVICE_MODEL.GET(),
    getDeviceName: () => LOCAL_STORAGE.DEVICE_NAME.GET(),
    getDeviceIp: () => LOCAL_STORAGE.DEVICE_IP.GET(),
  },
  [PLATFORMS.TIZEN]: {
    isLauncher: false,
    isStandalone: true,
    supportsAppLaunching: true,
    supportsBackButton: true,
    exitApp: () => window.tizen?.application?.getCurrentApplication()?.exit(),
    goToHome: () => window.tizen?.application?.getCurrentApplication()?.hide(),
    getMacAddress: () => LOCAL_STORAGE.DEVICE_ID.GET(), // Using device ID as MAC for Tizen
    getAppVersion: () =>
      window.tizen?.application?.getCurrentApplication()?.appInfo?.version,
    getDeviceId: () => LOCAL_STORAGE.DEVICE_ID.GET(),
    getDeviceModel: () => LOCAL_STORAGE.DEVICE_MODEL.GET(),
    getDeviceName: () => LOCAL_STORAGE.DEVICE_NAME.GET(),
    getDeviceIp: () => LOCAL_STORAGE.DEVICE_IP.GET(),
  },
  [PLATFORMS.WEB]: {
    isLauncher: false,
    isStandalone: true,
    supportsAppLaunching: false,
    supportsBackButton: false,
    exitApp: () => window.close(),
    goToHome: () => null,
    getMacAddress: () => LOCAL_STORAGE.DEVICE_ID.GET(),
    getAppVersion: () => null,
    getDeviceId: () => LOCAL_STORAGE.DEVICE_ID.GET(),
    getDeviceModel: () => LOCAL_STORAGE.DEVICE_MODEL.GET(),
    getDeviceName: () => LOCAL_STORAGE.DEVICE_NAME.GET(),
    getDeviceIp: () => LOCAL_STORAGE.DEVICE_IP.GET(),
  },
};

// Get current platform configuration
export const currentPlatformConfig = platformConfig[currentPlatform];

// Helper functions
export const isLauncherMode = () => {
  if (window.Android) {
    if (
      window.Android.isDefaultLauncher &&
      window.Android.isDefaultLauncher()
    ) {
      return true;
    }
    if (!window.Android.isDefaultLauncher) {
      return true;
    }
    return false;
  }

  // Check URL parameter first
  const urlParams = new URLSearchParams(window.location.search);
  const isLauncherParam = urlParams.get("isLauncher");

  if (isLauncherParam !== null) {
    return isLauncherParam === "true";
  }

  // Fallback to platform config
  return currentPlatformConfig.isLauncher;
};

export const isStandaloneMode = () => {
  return currentPlatformConfig.isStandalone;
};

// Check if launcher functionality is enabled from app_settings
export const isLauncherEnabled = () => {
  try {
    // Only check app_settings.launcher from localStorage also check if the platform is android
    if (currentPlatform !== PLATFORMS.ANDROID) {
      return false;
    }

    const appSettings = localStorage.getItem("app_settings");
    if (appSettings) {
      const settings = JSON.parse(appSettings);
      return settings.launcher === true;
    }

    // Default to false if no app_settings found
    return false;
  } catch (err) {
    console.log("Error checking launcher enabled status:", err);
    // Default to false if there's an error
    return false;
  }
};
